import React, { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/layout/breadcrumb";
import { FileText, ChevronLeft, Printer, ThumbsUp, ThumbsDown } from "lucide-react";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

// Articles data
const articles = {
  "1": {
    id: 1,
    title: "Getting started with SaaSGama",
    category: "Getting Started",
    content: `
      <h2>Welcome to SaaSGama</h2>
      <p class="mb-4">This guide will help you get started with SaaSGama, a comprehensive platform designed to help organizations track, manage, and optimize their software subscriptions and licenses.</p>
      
      <h3 class="mt-6 mb-3">What is SaaSGama?</h3>
      <p class="mb-4">SaaSGama is a powerful tool that provides complete visibility into your organization's software portfolio. It helps you track licenses, monitor usage, manage renewals, control costs, and optimize your software investments.</p>
      
      <h3 class="mt-6 mb-3">Key Features</h3>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2"><strong>License Management:</strong> Track all your software licenses in one place, including subscription details, renewal dates, and costs.</li>
        <li class="mb-2"><strong>User Assignment:</strong> Assign licenses to users and departments to monitor usage and ensure compliance.</li>
        <li class="mb-2"><strong>Cost Tracking:</strong> Track spending across different applications, departments, and time periods.</li>
        <li class="mb-2"><strong>Renewal Alerts:</strong> Get notified about upcoming renewals to avoid unexpected service disruptions.</li>
        <li class="mb-2"><strong>Optimization Recommendations:</strong> Receive data-driven recommendations to optimize your software portfolio and reduce unnecessary spending.</li>
        <li class="mb-2"><strong>Multi-Currency Support:</strong> Track costs in multiple currencies with automatic conversion for reporting.</li>
      </ul>
      
      <h3 class="mt-6 mb-3">First Steps</h3>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2"><strong>Complete Your Profile:</strong> Update your profile information and preferences in the account settings.</li>
        <li class="mb-2"><strong>Add Your Applications:</strong> Start by adding the software applications your organization uses. You can do this manually or import from a CSV file.</li>
        <li class="mb-2"><strong>Add License Information:</strong> For each application, add details about your licenses, including costs, number of seats, and renewal dates.</li>
        <li class="mb-2"><strong>Invite Team Members:</strong> Invite your colleagues to join SaaSGama to collaborate on managing your software portfolio.</li>
        <li class="mb-2"><strong>Explore the Dashboard:</strong> Familiarize yourself with the dashboard to get an overview of your software investments and usage metrics.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Next Steps</h3>
      <p class="mb-4">After completing the initial setup, you can explore more advanced features:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Set up cost allocation to different departments or cost centers</li>
        <li class="mb-2">Configure custom alerts for important events</li>
        <li class="mb-2">Generate detailed reports for stakeholders</li>
        <li class="mb-2">Review optimization recommendations to identify potential savings</li>
      </ul>
      
      <div class="bg-blue-50 border border-blue-200 rounded-md p-4 mt-6">
        <h4 class="text-blue-700 font-medium mb-2">Pro Tip</h4>
        <p class="text-blue-600">Don't try to add all your applications at once. Start with your most important or expensive applications to get immediate value, then gradually add the rest over time.</p>
      </div>
    `,
    tags: ["Getting Started", "Overview", "Beginner"],
    lastUpdated: "April 15, 2025",
    views: 2345,
    helpful: 187,
    notHelpful: 12,
    relatedArticles: [2, 3, 5]
  },
  "2": {
    id: 2,
    title: "How to import application data",
    category: "Data Management",
    content: `
      <h2>Importing Application Data</h2>
      <p class="mb-4">SaaSGama allows you to import application data in bulk using CSV files. This guide will walk you through the process of preparing and importing your data.</p>
      
      <h3 class="mt-6 mb-3">Preparing Your CSV File</h3>
      <p class="mb-4">Your CSV file should include the following columns:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2"><strong>name:</strong> Application name (required)</li>
        <li class="mb-2"><strong>vendor:</strong> Vendor or publisher name (required)</li>
        <li class="mb-2"><strong>category:</strong> Application category (e.g., Productivity, Communication, Design)</li>
        <li class="mb-2"><strong>description:</strong> Brief description of the application</li>
        <li class="mb-2"><strong>website:</strong> Application website URL</li>
        <li class="mb-2"><strong>status:</strong> Current status (Active, Inactive, Planned, etc.)</li>
      </ul>
      
      <div class="bg-gray-100 p-4 rounded-md mb-4 overflow-x-auto">
        <code>name,vendor,category,description,website,status</code><br>
        <code>Microsoft 365,Microsoft,Productivity,Office suite and collaboration platform,https://www.microsoft.com/microsoft-365,Active</code><br>
        <code>Slack,Slack Technologies,Communication,Team messaging platform,https://slack.com,Active</code><br>
        <code>Adobe Creative Cloud,Adobe,Design,Creative suite for designers,https://www.adobe.com/creativecloud.html,Active</code>
      </div>
      
      <h3 class="mt-6 mb-3">Import Process</h3>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Navigate to the <strong>Data Import</strong> page from the main menu.</li>
        <li class="mb-2">Select <strong>Applications</strong> as the data type to import.</li>
        <li class="mb-2">Click <strong>Choose File</strong> and select your CSV file.</li>
        <li class="mb-2">Review the preview to ensure your data is being mapped correctly.</li>
        <li class="mb-2">Click <strong>Import</strong> to begin the import process.</li>
        <li class="mb-2">Once complete, you'll see a summary of the imported records.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Handling Errors</h3>
      <p class="mb-4">If there are errors during the import, SaaSGama will provide an error report. Common issues include:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2"><strong>Missing required fields:</strong> Ensure all required fields are included.</li>
        <li class="mb-2"><strong>Duplicate entries:</strong> Applications with the same name will be flagged as potential duplicates.</li>
        <li class="mb-2"><strong>Invalid format:</strong> Check that your CSV is properly formatted with commas as separators.</li>
      </ul>
      
      <h3 class="mt-6 mb-3">Importing License Data</h3>
      <p class="mb-4">After importing applications, you can also import license data. The process is similar, but your CSV should include different columns:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2"><strong>application_name:</strong> Name of the application (must match exactly)</li>
        <li class="mb-2"><strong>name:</strong> License name or plan (e.g., "Business Premium")</li>
        <li class="mb-2"><strong>type:</strong> License type (Subscription, Perpetual, etc.)</li>
        <li class="mb-2"><strong>total_seats:</strong> Total number of seats or licenses</li>
        <li class="mb-2"><strong>cost:</strong> Cost per seat or total cost</li>
        <li class="mb-2"><strong>billing_cycle:</strong> Billing frequency (Monthly, Annual, etc.)</li>
        <li class="mb-2"><strong>start_date:</strong> License start date (YYYY-MM-DD)</li>
        <li class="mb-2"><strong>renewal_date:</strong> License renewal date (YYYY-MM-DD)</li>
      </ul>
      
      <div class="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-6">
        <h4 class="text-yellow-700 font-medium mb-2">Important Note</h4>
        <p class="text-yellow-600">Before importing license data, make sure all referenced applications are already in the system. Otherwise, the import will fail for licenses associated with unknown applications.</p>
      </div>
    `,
    tags: ["Data Import", "CSV", "Bulk Operations"],
    lastUpdated: "March 28, 2025",
    views: 1987,
    helpful: 156,
    notHelpful: 8,
    relatedArticles: [1, 4, 3]
  },
  "3": {
    id: 3,
    title: "Setting up license renewal alerts",
    category: "License Management",
    content: `
      <h2>Setting Up License Renewal Alerts</h2>
      <p class="mb-4">License renewal alerts help you stay on top of upcoming renewals to avoid service disruptions and plan your budget effectively. This guide will show you how to configure these alerts in SaaSGama.</p>
      
      <h3 class="mt-6 mb-3">Why Renewal Alerts Matter</h3>
      <p class="mb-4">Tracking renewal dates manually can be challenging, especially when managing dozens or hundreds of licenses. Missing a renewal deadline can result in:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Service interruptions affecting business operations</li>
        <li class="mb-2">Missed opportunities to negotiate better terms</li>
        <li class="mb-2">Unexpected urgent expenses</li>
        <li class="mb-2">Automatic renewals of services you no longer need</li>
      </ul>
      
      <h3 class="mt-6 mb-3">Default Alert Settings</h3>
      <p class="mb-4">By default, SaaSGama sends renewal notifications at the following intervals:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">90 days before renewal (for annual or multi-year subscriptions)</li>
        <li class="mb-2">30 days before renewal (for all subscriptions)</li>
        <li class="mb-2">7 days before renewal (for all subscriptions)</li>
        <li class="mb-2">1 day before renewal (for all subscriptions)</li>
      </ul>
      
      <h3 class="mt-6 mb-3">Customizing Alert Settings</h3>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Navigate to <strong>Settings > Notifications</strong> from the main menu.</li>
        <li class="mb-2">In the <strong>Renewal Alerts</strong> section, click <strong>Customize</strong>.</li>
        <li class="mb-2">You can now:
          <ul class="list-disc pl-6 mt-2">
            <li>Add or remove time intervals for alerts</li>
            <li>Set minimum cost thresholds for alerts (e.g., only alert for renewals over $1,000)</li>
            <li>Specify which team members should receive which alerts</li>
          </ul>
        </li>
        <li class="mb-2">Click <strong>Save Changes</strong> when done.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Setting Up Email Notifications</h3>
      <p class="mb-4">To receive alerts via email:</p>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Go to your <strong>Profile Settings</strong>.</li>
        <li class="mb-2">Ensure your email address is correctly entered and verified.</li>
        <li class="mb-2">In the <strong>Notification Preferences</strong> section, enable <strong>Email Notifications</strong>.</li>
        <li class="mb-2">Select which types of alerts you want to receive via email.</li>
        <li class="mb-2">Click <strong>Save Preferences</strong>.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Setting Up License-Specific Alerts</h3>
      <p class="mb-4">For critical licenses, you can set up custom alerts:</p>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Navigate to the specific license details page.</li>
        <li class="mb-2">Click on the <strong>Alerts</strong> tab.</li>
        <li class="mb-2">Click <strong>Add Custom Alert</strong>.</li>
        <li class="mb-2">Configure the alert timing and recipients.</li>
        <li class="mb-2">Click <strong>Save</strong>.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Viewing Upcoming Renewals</h3>
      <p class="mb-4">To view all upcoming renewals at once:</p>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Click on <strong>Renewals</strong> in the main navigation.</li>
        <li class="mb-2">Use filters to narrow down by time period, application type, or cost.</li>
        <li class="mb-2">Click on any renewal to see detailed information and take action.</li>
      </ol>
      
      <div class="bg-green-50 border border-green-200 rounded-md p-4 mt-6">
        <h4 class="text-green-700 font-medium mb-2">Best Practice</h4>
        <p class="text-green-600">Set up a renewal review meeting with stakeholders 60-90 days before major renewals to decide whether to renew, upgrade, downgrade, or cancel the subscription. This gives you enough time to evaluate alternatives if needed.</p>
      </div>
    `,
    tags: ["Renewals", "Alerts", "Notifications", "License Management"],
    lastUpdated: "April 10, 2025",
    views: 1789,
    helpful: 132,
    notHelpful: 5,
    relatedArticles: [4, 1, 5]
  },
  "4": {
    id: 4,
    title: "Understanding optimization recommendations",
    category: "Cost Optimization",
    content: `
      <h2>Understanding Optimization Recommendations</h2>
      <p class="mb-4">SaaSGama analyzes your software usage and spending patterns to generate data-driven recommendations for optimizing your software portfolio. This guide explains how these recommendations work and how to implement them effectively.</p>
      
      <h3 class="mt-6 mb-3">How Recommendations Are Generated</h3>
      <p class="mb-4">SaaSGama's recommendation engine uses several data points to identify optimization opportunities:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2"><strong>License utilization:</strong> Comparing assigned seats to total seats</li>
        <li class="mb-2"><strong>User activity:</strong> Analyzing how frequently users access applications</li>
        <li class="mb-2"><strong>Duplicate functionality:</strong> Identifying overlapping tools across your organization</li>
        <li class="mb-2"><strong>Cost trends:</strong> Monitoring spending increases over time</li>
        <li class="mb-2"><strong>License type analysis:</strong> Comparing current license types to usage patterns</li>
      </ul>
      
      <h3 class="mt-6 mb-3">Types of Recommendations</h3>
      <p class="mb-4">SaaSGama provides several types of optimization recommendations:</p>
      
      <h4 class="mt-4 mb-2">1. Right-sizing Opportunities</h4>
      <p class="mb-4">These recommendations identify licenses with low utilization where you could reduce the number of seats.</p>
      <div class="bg-gray-100 p-4 rounded-md mb-4">
        <p class="font-semibold">Example:</p>
        <p>"Adobe Creative Cloud has 50 licenses but only 32 are assigned. Consider reducing by 18 seats to save approximately $10,800 annually."</p>
      </div>
      
      <h4 class="mt-4 mb-2">2. License Downgrades</h4>
      <p class="mb-4">These suggestions identify users who have premium licenses but only use basic features.</p>
      <div class="bg-gray-100 p-4 rounded-md mb-4">
        <p class="font-semibold">Example:</p>
        <p>"10 users with Microsoft 365 E5 licenses ($38/month) only use email and basic Office applications. Consider downgrading to Microsoft 365 Business Basic ($5/month) to save approximately $3,960 annually."</p>
      </div>
      
      <h4 class="mt-4 mb-2">3. Duplicate Application Consolidation</h4>
      <p class="mb-4">These recommendations identify cases where your organization is using multiple tools with similar functionality.</p>
      <div class="bg-gray-100 p-4 rounded-md mb-4">
        <p class="font-semibold">Example:</p>
        <p>"Your organization is using both Asana and Trello for project management. Consider consolidating to a single platform to save approximately $4,200 annually."</p>
      </div>
      
      <h4 class="mt-4 mb-2">4. Billing Optimization</h4>
      <p class="mb-4">These suggestions identify opportunities to save by changing billing cycles or payment methods.</p>
      <div class="bg-gray-100 p-4 rounded-md mb-4">
        <p class="font-semibold">Example:</p>
        <p>"Switching from monthly to annual billing for Salesforce would save approximately 20% or $12,000 annually."</p>
      </div>
      
      <h3 class="mt-6 mb-3">Implementing Recommendations</h3>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2"><strong>Review the details:</strong> Click on any recommendation to see the underlying data and analysis.</li>
        <li class="mb-2"><strong>Validate with stakeholders:</strong> Consult with department heads or power users before making changes.</li>
        <li class="mb-2"><strong>Create an action plan:</strong> Decide which recommendations to implement and when.</li>
        <li class="mb-2"><strong>Track savings:</strong> After implementing a recommendation, SaaSGama will track the actual savings compared to the projected amount.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Recommendation Settings</h3>
      <p class="mb-4">You can customize how recommendations are generated:</p>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Go to <strong>Settings > Recommendations</strong>.</li>
        <li class="mb-2">Adjust thresholds for what constitutes underutilization (e.g., less than 80% of seats assigned).</li>
        <li class="mb-2">Set minimum cost thresholds for recommendations (e.g., only suggest changes that save at least $1,000).</li>
        <li class="mb-2">Specify which types of recommendations you want to receive.</li>
      </ol>
      
      <div class="bg-purple-50 border border-purple-200 rounded-md p-4 mt-6">
        <h4 class="text-purple-700 font-medium mb-2">Pro Tip</h4>
        <p class="text-purple-600">Generate a quarterly optimization report to share with executives. This demonstrates the ROI of your SaaS management efforts and helps build support for implementing recommendations.</p>
      </div>
    `,
    tags: ["Cost Optimization", "Recommendations", "ROI", "Cost Savings"],
    lastUpdated: "April 5, 2025",
    views: 1654,
    helpful: 118,
    notHelpful: 7,
    relatedArticles: [3, 2, 5]
  },
  "5": {
    id: 5,
    title: "Managing user permissions",
    category: "User Management",
    content: `
      <h2>Managing User Permissions</h2>
      <p class="mb-4">SaaSGama offers a flexible permission system that allows you to control what different users can see and do within the platform. This guide explains how to set up and manage user permissions effectively.</p>
      
      <h3 class="mt-6 mb-3">User Roles Overview</h3>
      <p class="mb-4">SaaSGama provides several predefined roles with different permission levels:</p>
      <table class="w-full border-collapse mb-4">
        <thead>
          <tr class="bg-gray-100">
            <th class="border p-2 text-left">Role</th>
            <th class="border p-2 text-left">Description</th>
            <th class="border p-2 text-left">Typical User</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="border p-2"><strong>Admin</strong></td>
            <td class="border p-2">Full access to all features and settings</td>
            <td class="border p-2">IT Managers, SaaS Admins</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Manager</strong></td>
            <td class="border p-2">Can manage applications and licenses, but cannot modify system settings</td>
            <td class="border p-2">IT Team Members, Department Heads</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Finance</strong></td>
            <td class="border p-2">Read access to cost information and reports, cannot modify data</td>
            <td class="border p-2">Finance Team, CFO</td>
          </tr>
          <tr>
            <td class="border p-2"><strong>Viewer</strong></td>
            <td class="border p-2">Read-only access to basic information, cannot see sensitive cost data</td>
            <td class="border p-2">Team Members, Department Managers</td>
          </tr>
        </tbody>
      </table>
      
      <h3 class="mt-6 mb-3">Assigning User Roles</h3>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Go to <strong>Users</strong> in the main navigation.</li>
        <li class="mb-2">Find the user you want to modify and click on their name.</li>
        <li class="mb-2">In the user details page, click <strong>Edit</strong>.</li>
        <li class="mb-2">From the <strong>Role</strong> dropdown, select the appropriate role.</li>
        <li class="mb-2">Click <strong>Save Changes</strong>.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Creating Custom Roles</h3>
      <p class="mb-4">If the predefined roles don't meet your needs, you can create custom roles:</p>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Go to <strong>Settings > User Roles</strong>.</li>
        <li class="mb-2">Click <strong>Create New Role</strong>.</li>
        <li class="mb-2">Enter a name and description for the role.</li>
        <li class="mb-2">Configure permissions by checking or unchecking options in each category:
          <ul class="list-disc pl-6 mt-2">
            <li><strong>Applications:</strong> View, Create, Edit, Delete</li>
            <li><strong>Licenses:</strong> View, Create, Edit, Delete, Assign</li>
            <li><strong>Users:</strong> View, Create, Edit, Delete</li>
            <li><strong>Cost Data:</strong> View Summary, View Details, Export</li>
            <li><strong>Reports:</strong> View, Create, Edit, Schedule</li>
            <li><strong>Settings:</strong> View, Modify</li>
          </ul>
        </li>
        <li class="mb-2">Click <strong>Save Role</strong>.</li>
      </ol>
      
      <h3 class="mt-6 mb-3">Department-Based Permissions</h3>
      <p class="mb-4">You can restrict users to only see data related to their department:</p>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Go to <strong>Settings > Departments</strong>.</li>
        <li class="mb-2">Create or edit departments as needed.</li>
        <li class="mb-2">When editing a user, assign them to one or more departments.</li>
        <li class="mb-2">Enable the <strong>Department Filtering</strong> option for the user's role.</li>
      </ol>
      <p class="mb-4">With department filtering enabled, users will only see applications, licenses, and cost data associated with their assigned departments.</p>
      
      <h3 class="mt-6 mb-3">Permission Audit</h3>
      <p class="mb-4">It's good practice to regularly review user permissions:</p>
      <ol class="list-decimal pl-6 mb-4">
        <li class="mb-2">Go to <strong>Settings > Security</strong>.</li>
        <li class="mb-2">Click <strong>Permission Audit Report</strong>.</li>
        <li class="mb-2">Review the list of users and their current permissions.</li>
        <li class="mb-2">Make adjustments as necessary to ensure proper access control.</li>
      </ol>
      
      <div class="bg-red-50 border border-red-200 rounded-md p-4 mt-6">
        <h4 class="text-red-700 font-medium mb-2">Security Best Practice</h4>
        <p class="text-red-600">Follow the principle of least privilege: only give users the minimum permissions they need to perform their job functions. Regularly review admin accounts to ensure only necessary staff have elevated privileges.</p>
      </div>
    `,
    tags: ["User Management", "Permissions", "Security", "Access Control"],
    lastUpdated: "March 15, 2025",
    views: 1423,
    helpful: 98,
    notHelpful: 3,
    relatedArticles: [1, 2, 3]
  }
};

const categoryPages: Record<string, { title: string; description: string; articleIds: number[] }> = {
  "getting-started": {
    title: "Getting Started",
    description: "Start here for setup, imports, renewal tracking, and optimization basics.",
    articleIds: [1, 2, 3, 4],
  },
  "account-settings": {
    title: "Account Settings",
    description: "Manage profile settings, roles, permissions, and account access.",
    articleIds: [1, 5],
  },
  "licensing": {
    title: "Licensing & Permissions",
    description: "Learn how license records, renewal alerts, assignments, and permissions work.",
    articleIds: [3],
  },
  "license-management": {
    title: "Licensing & Permissions",
    description: "Learn how license records, renewal alerts, assignments, and permissions work.",
    articleIds: [3],
  },
  "billing": {
    title: "Billing & Payments",
    description: "Understand billing cycles, renewal costs, payment tracking, and imports.",
    articleIds: [2],
  },
  "users": {
    title: "User Management",
    description: "Set up roles, permissions, and access rules for your organization.",
    articleIds: [5],
  },
  "user-management": {
    title: "User Management",
    description: "Set up roles, permissions, and access rules for your organization.",
    articleIds: [5],
  },
  "troubleshooting": {
    title: "Troubleshooting",
    description: "Resolve common setup, import, renewal, and access issues.",
    articleIds: [2, 3, 5],
  },
  "data-management": {
    title: "Data Management",
    description: "Import, validate, and maintain application and license records.",
    articleIds: [2],
  },
  "cost-optimization": {
    title: "Cost Optimization",
    description: "Use recommendations and usage data to reduce SaaS spend.",
    articleIds: [4],
  },
};

export default function HelpArticle() {
  const [location] = useLocation();
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);
  const paths = location.split('/');
  const articleId = paths[paths.length - 1];
  const isArticleRoute = location.includes('/docs/article/');
  
  // Find the article based on the ID from the URL
  const article = isArticleRoute ? articles[articleId as keyof typeof articles] : undefined;
  const categoryPage = !isArticleRoute ? categoryPages[articleId] : undefined;

  if (categoryPage) {
    return (
      <div>
        <Breadcrumb items={[
          { label: "Home", href: "/" },
          { label: "Help Center", href: "/help" },
          { label: categoryPage.title, href: location }
        ]} />

        <div className="my-6">
          <Button asChild variant="outline" size="sm" className="mb-4">
            <a href="/help">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Help Center
            </a>
          </Button>
          <h2 className="text-2xl font-bold text-slate-800">{categoryPage.title}</h2>
          <p className="text-sm text-slate-500 mt-1">{categoryPage.description}</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {categoryPage.articleIds.map(id => {
            const categoryArticle = articles[id.toString() as keyof typeof articles];
            if (!categoryArticle) return null;

            return (
              <Card key={id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-primary/10 text-primary">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{categoryArticle.title}</CardTitle>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge variant="outline">{categoryArticle.category}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {categoryArticle.views.toLocaleString()} views
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Last updated: {categoryArticle.lastUpdated}
                  </p>
                  <Button asChild variant="outline" className="w-full">
                    <a href={`/docs/article/${categoryArticle.id}`}>Read Article</a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }
  
  // If article not found, display a message
  if (!article) {
    return (
      <div>
        <Breadcrumb items={[
          { label: "Home", href: "/" },
          { label: "Help Center", href: "/help" },
          { label: "Article Not Found", href: location }
        ]} />
        
        <div className="my-6">
          <h2 className="text-2xl font-bold text-slate-800">Article Not Found</h2>
          <p className="text-sm text-slate-500 mt-1">The article you're looking for doesn't exist or has been moved.</p>
        </div>
        
        <Button asChild variant="outline">
          <a href="/help">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Help Center
          </a>
        </Button>
      </div>
    );
  }
  
  // If article is found, display it
  return (
    <div>
      <Breadcrumb items={[
        { label: "Home", href: "/" },
        { label: "Help Center", href: "/help" },
        { label: article.category, href: `/docs/${article.category.toLowerCase().replace(/\s+/g, '-')}` },
        { label: article.title, href: location }
      ]} />
      
      <div className="flex justify-between items-start my-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{article.title}</h2>
          <div className="flex items-center gap-3 mt-2">
            <Badge variant="outline" className="text-xs">
              {article.category}
            </Badge>
            <span className="text-xs text-muted-foreground">
              Last updated: {article.lastUpdated}
            </span>
            <span className="text-xs text-muted-foreground">
              {article.views.toLocaleString()} views
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </div>
      </div>
      
      <div className="grid gap-6 md:grid-cols-4">
        <div className="md:col-span-3">
          <Card>
            <CardContent className="pt-6">
              <article className="prose prose-slate max-w-none">
                <div dangerouslySetInnerHTML={{ __html: article.content }} />
              </article>
              
              <div className="mt-10 pt-6 border-t">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Was this article helpful?</p>
                    {helpful === null ? (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => setHelpful(true)}>
                          <ThumbsUp className="h-4 w-4" />
                          Yes
                        </Button>
                        <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => setHelpful(false)}>
                          <ThumbsDown className="h-4 w-4" />
                          No
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{helpful ? "Thanks for the feedback!" : "Sorry to hear that. We'll work on improving this."}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Share this article</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}>
                        {copied ? "Copied!" : "Copy Link"}
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(window.location.href)}`}>Email</a>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Related Articles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {article.relatedArticles.map(relId => {
                  const relatedArticle = articles[relId.toString() as keyof typeof articles];
                  if (!relatedArticle) return null;
                  return (
                    <div key={relId} className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                      <a href={`/docs/article/${relId.toString()}`} className="text-sm hover:text-primary transition-colors line-clamp-1">
                        {relatedArticle.title}
                      </a>
                    </div>
                  );
                })}
              </div>
              
              <Separator className="my-4" />
              
              <div className="space-y-2">
                {article.tags.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="mr-2">
                    {tag}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
